import { FC, Fragment } from 'react'
import { ReactComponent as LightbulbIcon } from 'src/assets/svg/lightbulb-line-24.svg'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { ReactComponent as ReviewsIcon } from 'src/assets/svg/carbon_review.svg'
import { ReactComponent as SmileIcon } from 'src/assets/svg/uil_smile.svg'
import { ReactComponent as SpeedometerIcon } from 'src/assets/svg/ion_speedometer-outline.svg'
import { ReactComponent as StarIcon } from 'src/assets/svg/mynaui_star.svg'
import {
  ReviewComparisonFacility,
  ReviewComparisonMetric,
} from 'src/services/reviewAnalysisApi/interfaces/reviewComparison.interface'
import styles from './reviewKeyMetrics.module.scss'
import { FACILITY_COLORS, facilityColor } from '../palette'

interface ReviewKeyMetricsProps {
  facilities: ReviewComparisonFacility[]
  insight: string
  metrics: ReviewComparisonMetric[]
}

const COPY = {
  isBetter: 'is better',
  level: 'Level',
  // One side has nothing to measure, so there is no comparison to state —
  // which is not the same as the two being level.
  notComparable: 'Nothing to compare',
  subtitle: 'Headline volume, rating and sentiment, matched side by side',
  title: 'Key Metrics Comparison',
}

// Keyed by position rather than by name: the four cards are fixed, and the data
// stays free of components.
const ICONS = [ReviewsIcon, StarIcon, SmileIcon, SpeedometerIcon]

// Which of the two columns the winner is standing in. The arrow points at it
// — left for the first facility, right for the second — so the row says who
// won by where it points, not only by the name it reads out.
const winnerIndex = (metric: ReviewComparisonMetric) =>
  metric.values.findIndex(
    (value) => value.facilityId === metric.betterFacilityId
  )

// The gap under a card. One arrow, on the winner's side, in the winner's
// colour; the amount in green because it is always the margin in their favour.
// It used to carry an arrow at BOTH ends, which pointed at nothing.
const deltaOf = (metric: ReviewComparisonMetric) => {
  if (!metric.betterFacilityName) {
    return metric.values.every((value) => value.value !== null)
      ? COPY.level
      : COPY.notComparable
  }

  const index = winnerIndex(metric)
  // A winner the values do not contain would leave the arrow with no side to
  // be on; the row still reads correctly without one.
  const arrow =
    index < 0 ? null : (
      <span
        className={styles.deltaArrow}
        style={{ color: facilityColor(index) }}
      >
        {index === 0 ? '←' : '→'}
      </span>
    )

  const body = (
    <>
      <span className={styles.deltaAmount}>{metric.betterBy}</span>{' '}
      {metric.betterFacilityName} {COPY.isBetter}
    </>
  )

  return index === 0 ? (
    <>
      {arrow} {body}
    </>
  ) : (
    <>
      {body} {arrow}
    </>
  )
}

// The two facilities side by side, and under them the gap between them named
// for the one it favours — which is the comparison, rather than the pair of
// numbers above it.
export const ReviewKeyMetrics: FC<ReviewKeyMetricsProps> = ({
  facilities,
  insight,
  metrics,
}) => (
  <section className={styles.section}>
    <div className={styles.header}>
      <div className={styles.headerText}>
        <h3 className={styles.title}>{COPY.title}</h3>
        <p className={styles.subtitle}>{COPY.subtitle}</p>
      </div>

      <div className={styles.legend}>
        {facilities.map((facility, index) => (
          <span className={styles.legendItem} key={facility.facilityId}>
            <span
              className={styles.legendSwatch}
              style={{ background: FACILITY_COLORS[index] }}
            />
            <TruncatedTooltip text={facility.facilityName} />
          </span>
        ))}
      </div>
    </div>

    <div className={styles.cards}>
      {metrics.map((metric, index) => {
        const Icon = ICONS[index] ?? ReviewsIcon

        return (
          <article className={styles.card} key={metric.label}>
            <div className={styles.cardHeader}>
              <Icon className={styles.cardIcon} />
              <span className={styles.cardLabel}>{metric.label}</span>
            </div>

            <div className={styles.values}>
              {metric.values.map((value, valueIndex) => (
                <Fragment key={value.facilityId}>
                  {/* Between the two, not after each: the rule separates the
                      facilities rather than decorating them. */}
                  {valueIndex > 0 && <span className={styles.valuesDivider} />}

                  <div className={styles.value}>
                    <span
                      className={styles.valueNumber}
                      style={{ color: FACILITY_COLORS[valueIndex] }}
                    >
                      {value.display}
                    </span>
                    <TruncatedTooltip
                      className={styles.valueFacility}
                      text={value.facilityName}
                    />
                  </div>
                </Fragment>
              ))}
            </div>

            {/* Three things the gap can be: a winner, a tie, or nothing to
                compare because one side has no reading at all. Saying "Level"
                for the third would claim a tie between 4.9 and no data. */}
            <span className={styles.delta}>{deltaOf(metric)}</span>
          </article>
        )
      })}
    </div>

    <div className={styles.insight}>
      <LightbulbIcon className={styles.insightIcon} />
      <p className={styles.insightText}>{insight}</p>
    </div>
  </section>
)
